// src/components/dashboard/MissingExercisesStats.tsx
import React, { useState, useEffect } from "react";
import {
    Typography,
    Box,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    ListItemText,
    Divider,
    Chip,
    Button,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SportsGymnasticsIcon from "@mui/icons-material/SportsGymnastics";
import AssignmentLateIcon from "@mui/icons-material/AssignmentLate";
import GroupsIcon from "@mui/icons-material/Groups";
import SendIcon from "@mui/icons-material/Send";
import axios from "../../utils/axiosConfig";

import logger from "../../utils/logger";

interface MissingAthlete {
  id: string;
  name: string;
  age: number;
  gender: string;
  competitionGroup: string;
}

interface ClubStats {
  clubName: string;
  totalAthletes: number;
  missingExercises: number;
  athletes: MissingAthlete[];
}

interface MissingExercisesData {
  totalAthletes: number;
  totalMissing: number;
  missingPercentage: number;
  clubStats: ClubStats[];
}

interface MissingExercisesStatsProps {
  competitionId: string;
}

const MissingExercisesStats: React.FC<MissingExercisesStatsProps> = ({ competitionId }) => {
    const [expanded, setExpanded] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [statsData, setStatsData] = useState<MissingExercisesData | null>(null);

    const handleExpandToggle = () => {
        setExpanded(!expanded);
    };

    useEffect(() => {
        const fetchMissingExercisesData = async () => {
            if (!competitionId) return;

            try {
                setLoading(true);
                const response = await axios.get(`/api/admin/exercises/missing-exercises/${competitionId}`);
                setStatsData(response.data);
            } catch (err) {
                //console.error('Error fetching missing exercises data:', err);
                logger.error('Error fetching missing exercises data:', {error: err});
                setError('Fehler beim Laden der Statistiken über fehlende Übungen');
            } finally {
                setLoading(false);
            }
        };

        fetchMissingExercisesData();
    }, [competitionId]);

    const getGenderLabel = (gender: string) => {
        switch (gender) {
            case 'm': return 'männlich';
            case 'w': return 'weiblich';
            default: return gender;
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" my={2}>
                <CircularProgress size={40} />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    if (!statsData || statsData.totalMissing === 0) {
        return (
            <Paper 
                elevation={2} 
                sx={{ 
                    my: 3, 
                    p: 2, 
                    backgroundColor: '#e8f5e9' // Light green background for success
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SportsGymnasticsIcon color="success" />
                    <Typography variant="h6">
                        Alle Sportler haben ihre Pflichtübungen gemeldet
                    </Typography>
                </Box>
            </Paper>
        );
    }

    return (
        <Paper elevation={2} sx={{ my: 3 }}>
            <Accordion 
                expanded={expanded} 
                onChange={handleExpandToggle}
                sx={{ boxShadow: 'none' }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                        backgroundColor: '#ffebee', // Light red background for warning
                        '&:hover': {
                            backgroundColor: '#ffcdd2',
                        },
                    }}
                >
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        width: '100%',
                        pr: 2
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AssignmentLateIcon color="error" />
                            <Typography variant="h6">
                                Fehlende Pflichtübungen / Wettkampfkarten
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Chip
                                icon={<GroupsIcon />}
                                label={`${statsData.clubStats.length} Vereine`}
                                color="primary"
                                size="small"
                            />
                            <Chip
                                icon={<AssignmentLateIcon />}
                                label={`${statsData.totalMissing} von ${statsData.totalAthletes} Athleten (${statsData.missingPercentage}%)`}
                                color="error"
                                size="small"
                            />
                        </Box>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    {statsData.clubStats.map((club, clubIndex) => (
                        <Accordion key={club.clubName} defaultExpanded={clubIndex === 0}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center',
                                    width: '100%',
                                    pr: 2
                                }}>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        {club.clubName}
                                    </Typography>
                                    <Chip
                                        color="error"
                                        size="small"
                                        label={`${club.missingExercises} von ${club.totalAthletes} fehlen (${Math.round(club.missingExercises / club.totalAthletes * 100)}%)`}
                                    />
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Name</TableCell>
                                                <TableCell>Geschlecht</TableCell>
                                                <TableCell>Jahrgang</TableCell>
                                                <TableCell>Wettkampfklasse</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {club.athletes.map((athlete) => (
                                                <TableRow key={athlete.id}>
                                                    <TableCell>{athlete.name}</TableCell>
                                                    <TableCell>{getGenderLabel(athlete.gender)}</TableCell>
                                                    <TableCell>{athlete.age}</TableCell>
                                                    <TableCell>{athlete.competitionGroup}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                    
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
                        <Button 
                            variant="outlined" 
                            color="error"
                            startIcon={<SendIcon />}
                            onClick={() => {
                                alert('Diese Funktion könnte E-Mails an die Vereine versenden, um sie an fehlende Pflichtübungen zu erinnern.');
                            }}
                        >
                            Erinnerung an alle versenden
                        </Button>
                    </Box>
                </AccordionDetails>
            </Accordion>
        </Paper>
    );
};

export default MissingExercisesStats;